import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, Package, MapPin, CreditCard, Gift, Phone, Truck, ShoppingBag, LogIn, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UI_TEXTS = {
  de: {
    headerTitle: 'Hermann Bohmer',
    headerSubtitle: 'Ihr personlicher Assistent',
    placeholder: 'Ihre Frage eingeben...',
    quickQuestionsLabel: 'Wie kann ich Ihnen helfen?',
    typing: 'Schreibt...',
    online: 'Online',
    loginPrompt: 'Bitte loggen Sie sich ein oder geben Sie Ihre Tracking-Nummer ein.',
    trackingPlaceholder: 'Tracking-Nummer eingeben...',
    searchTracking: 'Suchen',
    notLoggedIn: 'Sie sind nicht eingeloggt.',
    loginButton: 'Zum Login',
    welcomeBack: 'Willkommen zuruck',
    categories: 'Kategorien',
    products: 'Produkte',
    viewInShop: 'Im Shop ansehen',
    inStock: 'Auf Lager',
    outOfStock: 'Ausverkauft',
    freeShippingFrom: 'Gratis Versand ab',
    shippingCost: 'Versandkosten',
    orderStatus: 'Bestellstatus',
    trackingNumber: 'Tracking-Nummer',
    noOrdersFound: 'Keine Bestellungen gefunden.',
    yourOrders: 'Ihre Bestellungen',
    orderDate: 'Bestellt am',
    status: 'Status',
    total: 'Gesamt'
  },
  en: {
    headerTitle: 'Hermann Bohmer',
    headerSubtitle: 'Your Personal Assistant',
    placeholder: 'Type your question...',
    quickQuestionsLabel: 'How can I help you?',
    typing: 'Typing...',
    online: 'Online',
    loginPrompt: 'Please log in or enter your tracking number.',
    trackingPlaceholder: 'Enter tracking number...',
    searchTracking: 'Search',
    notLoggedIn: 'You are not logged in.',
    loginButton: 'Go to Login',
    welcomeBack: 'Welcome back',
    categories: 'Categories',
    products: 'Products',
    viewInShop: 'View in Shop',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    freeShippingFrom: 'Free shipping from',
    shippingCost: 'Shipping cost',
    orderStatus: 'Order Status',
    trackingNumber: 'Tracking Number',
    noOrdersFound: 'No orders found.',
    yourOrders: 'Your Orders',
    orderDate: 'Ordered on',
    status: 'Status',
    total: 'Total'
  }
};

const QUICK_ACTIONS = {
  de: [
    { icon: ShoppingBag, label: 'Was verkauft ihr?', action: 'products' },
    { icon: Truck, label: 'Versandkosten', action: 'shipping' },
    { icon: Package, label: 'Meine Bestellung', action: 'my_order' },
    { icon: MapPin, label: 'Standort & Kontakt', action: 'contact' },
    { icon: Gift, label: 'Aktuelle Rabatte', action: 'coupons' },
    { icon: CreditCard, label: 'Zahlungsarten', action: 'payment' }
  ],
  en: [
    { icon: ShoppingBag, label: 'What do you sell?', action: 'products' },
    { icon: Truck, label: 'Shipping costs', action: 'shipping' },
    { icon: Package, label: 'My order', action: 'my_order' },
    { icon: MapPin, label: 'Location & Contact', action: 'contact' },
    { icon: Gift, label: 'Current discounts', action: 'coupons' },
    { icon: CreditCard, label: 'Payment methods', action: 'payment' }
  ]
};

const STATUS_TRANSLATIONS = {
  de: {
    pending: 'Ausstehend',
    paid: 'Bezahlt',
    processing: 'In Bearbeitung',
    shipped: 'Versendet',
    delivered: 'Zugestellt',
    cancelled: 'Storniert'
  },
  en: {
    pending: 'Pending',
    paid: 'Paid',
    processing: 'Processing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled'
  }
};

const CATEGORY_NAMES = {
  de: {
    likoer: 'Likore',
    edelbrand: 'Edelbrande',
    marmelade: 'Marmeladen',
    chutney: 'Chutneys',
    schokolade: 'Schokoladen',
    pralinen: 'Pralinen',
    essig: 'Essige',
    honig: 'Honig',
    sirup: 'Sirupe',
    geschenkset: 'Geschenksets'
  },
  en: {
    likoer: 'Liqueurs',
    edelbrand: 'Fine Brandies',
    marmelade: 'Jams',
    chutney: 'Chutneys',
    schokolade: 'Chocolates',
    pralinen: 'Pralines',
    essig: 'Vinegars',
    honig: 'Honey',
    sirup: 'Syrups',
    geschenkset: 'Gift Sets'
  }
};

export default function ChatWidget() {
  const { language } = useLanguage();
  const { user, isLoggedIn, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [trackingInput, setTrackingInput] = useState('');
  const [showTrackingInput, setShowTrackingInput] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const texts = UI_TEXTS[language] || UI_TEXTS.de;
  const quickActions = QUICK_ACTIONS[language] || QUICK_ACTIONS.de;

  const getTypingDelay = useCallback(() => {
    return Math.floor(Math.random() * 2000) + 3000;
  }, []);

  const delay = useCallback((ms) => new Promise(resolve => setTimeout(resolve, ms)), []);

  const addBotMessage = useCallback((content, isHtml = false) => {
    const msg = {
      id: Date.now() + Math.random(),
      type: 'bot',
      content,
      isHtml,
      time: new Date()
    };
    setMessages(prev => [...prev, msg]);
    setIsTyping(false);
  }, []);

  const addUserMessage = useCallback((text) => {
    const msg = {
      id: Date.now(),
      type: 'user',
      content: text,
      time: new Date()
    };
    setMessages(prev => [...prev, msg]);
  }, []);

  const getInitialMessage = useCallback(() => {
    const greeting = language === 'de'
      ? `Guten Tag${isLoggedIn && user?.first_name ? `, ${user.first_name}` : ''}! Ich bin Ihr virtueller Assistent von Hermann Bohmer. Wie kann ich Ihnen heute behilflich sein?`
      : `Good day${isLoggedIn && user?.first_name ? `, ${user.first_name}` : ''}! I am your virtual assistant from Hermann Bohmer. How may I assist you today?`;
    return greeting;
  }, [language, isLoggedIn, user]);

  useEffect(() => {
    setMessages([{
      id: 1,
      type: 'bot',
      content: getInitialMessage(),
      time: new Date()
    }]);
  }, [language, getInitialMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      return res.data;
    } catch (e) {
      console.error('Error fetching products:', e);
      return [];
    }
  };

  const fetchShippingRates = async () => {
    try {
      const res = await axios.get(`${API}/shipping-rates`);
      return res.data;
    } catch (e) {
      console.error('Error fetching shipping rates:', e);
      return [];
    }
  };

  const fetchCustomerOrders = async () => {
    if (!isLoggedIn || !token) return [];
    try {
      const res = await axios.get(`${API}/customer/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data;
    } catch (e) {
      console.error('Error fetching orders:', e);
      return [];
    }
  };

  const fetchOrderByTracking = async (trackingNumber) => {
    try {
      const res = await axios.get(`${API}/tracking/${trackingNumber}`);
      return res.data;
    } catch (e) {
      console.error('Error fetching order:', e);
      return null;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(language === 'de' ? 'de-AT' : 'en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'de' ? 'de-AT' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleProductsQuery = async () => {
    setIsTyping(true);
    const products = await fetchProducts();
    await delay(getTypingDelay());

    if (!products || products.length === 0) {
      addBotMessage(language === 'de'
        ? 'Entschuldigung, ich konnte die Produktdaten nicht laden. Bitte besuchen Sie unseren Shop direkt.'
        : 'Sorry, I could not load the product data. Please visit our shop directly.');
      return;
    }

    const categories = {};
    products.forEach(p => {
      const cat = p.category || 'other';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(p);
    });

    const catNames = CATEGORY_NAMES[language] || CATEGORY_NAMES.de;

    let html = `<div class="space-y-3">
      <p class="font-medium">${language === 'de' ? 'Wir bieten Ihnen folgende Produktkategorien:' : 'We offer you the following product categories:'}</p>
      <div class="space-y-2">`;

    Object.entries(categories).forEach(([cat, prods]) => {
      const catName = catNames[cat] || cat;
      const inStockCount = prods.filter(p => p.stock > 0).length;
      html += `
        <div class="bg-[#F2EFE9] rounded-lg p-2">
          <div class="flex justify-between items-center">
            <span class="font-medium text-[#2D2A26]">${catName}</span>
            <span class="text-xs text-[#969088]">${prods.length} ${texts.products}</span>
          </div>
          <div class="text-xs text-[#5C5852] mt-1">${inStockCount} ${texts.inStock}</div>
        </div>`;
    });

    html += `</div>
      <div class="mt-3 pt-3 border-t border-[#E5E0D8]">
        <p class="text-sm text-[#5C5852]">${language === 'de'
          ? `Insgesamt haben wir <strong>${products.length} Produkte</strong> fur Sie im Angebot.`
          : `We have a total of <strong>${products.length} products</strong> available for you.`}</p>
        <a href="/shop" class="inline-block mt-2 text-sm text-[#8B2E2E] hover:underline font-medium">${texts.viewInShop} &rarr;</a>
      </div>
    </div>`;

    addBotMessage(html, true);
  };

  const handleShippingQuery = async () => {
    setIsTyping(true);
    const rates = await fetchShippingRates();
    await delay(getTypingDelay());

    if (!rates || rates.length === 0) {
      addBotMessage(language === 'de'
        ? 'Die Versandkosten werden im Warenkorb basierend auf Ihrem Land berechnet.'
        : 'Shipping costs are calculated in the cart based on your country.');
      return;
    }

    let html = `<div class="space-y-3">
      <p class="font-medium">${language === 'de' ? 'Unsere aktuellen Versandkosten:' : 'Our current shipping rates:'}</p>
      <div class="space-y-2">`;

    rates.filter(r => r.is_active).forEach(rate => {
      html += `
        <div class="bg-[#F2EFE9] rounded-lg p-2 flex justify-between items-center">
          <span class="font-medium text-[#2D2A26]">${rate.country}</span>
          <div class="text-right">
            <div class="text-sm font-medium text-[#8B2E2E]">${formatPrice(rate.rate)}</div>
            ${rate.free_shipping_threshold ? `<div class="text-xs text-green-600">${texts.freeShippingFrom} ${formatPrice(rate.free_shipping_threshold)}</div>` : ''}
          </div>
        </div>`;
    });

    html += `</div>
      <p class="text-xs text-[#969088] mt-2">${language === 'de'
        ? 'Lieferzeit: Osterreich 2-3 Werktage, Deutschland 3-5 Werktage, EU 5-7 Werktage.'
        : 'Delivery time: Austria 2-3 business days, Germany 3-5 business days, EU 5-7 business days.'}</p>
    </div>`;

    addBotMessage(html, true);
  };

  const handleMyOrderQuery = async () => {
    setIsTyping(true);

    if (!isLoggedIn) {
      await delay(getTypingDelay());
      setIsTyping(false);
      setShowTrackingInput(true);
      addBotMessage(language === 'de'
        ? `Sie sind derzeit nicht eingeloggt. Um Ihre Bestellungen einzusehen, haben Sie zwei Moglichkeiten:\n\n1. Loggen Sie sich in Ihr Kundenkonto ein\n2. Geben Sie Ihre Tracking-Nummer ein`
        : `You are currently not logged in. To view your orders, you have two options:\n\n1. Log in to your customer account\n2. Enter your tracking number`);
      return;
    }

    const orders = await fetchCustomerOrders();
    await delay(getTypingDelay());
    const statusTrans = STATUS_TRANSLATIONS[language] || STATUS_TRANSLATIONS.de;

    if (!orders || orders.length === 0) {
      addBotMessage(language === 'de'
        ? 'Sie haben noch keine Bestellungen aufgegeben. Stobern Sie gerne in unserem Shop!'
        : 'You have not placed any orders yet. Feel free to browse our shop!');
      return;
    }

    let html = `<div class="space-y-3">
      <p class="font-medium">${texts.yourOrders}:</p>
      <div class="space-y-2 max-h-48 overflow-y-auto">`;

    orders.slice(0, 5).forEach(order => {
      const statusColor = order.status === 'delivered' ? 'text-green-600'
        : order.status === 'shipped' ? 'text-blue-600'
        : order.status === 'cancelled' ? 'text-red-600'
        : 'text-amber-600';

      html += `
        <div class="bg-[#F2EFE9] rounded-lg p-2">
          <div class="flex justify-between items-start">
            <div>
              <div class="text-xs text-[#969088]">${texts.trackingNumber}</div>
              <div class="font-mono text-sm font-medium">${order.tracking_number}</div>
            </div>
            <div class="text-right">
              <div class="text-xs ${statusColor} font-medium">${statusTrans[order.status] || order.status}</div>
              <div class="text-sm font-medium text-[#8B2E2E]">${formatPrice(order.total_amount)}</div>
            </div>
          </div>
          <div class="text-xs text-[#969088] mt-1">${texts.orderDate}: ${formatDate(order.created_at)}</div>
        </div>`;
    });

    html += `</div>
      <a href="/account" class="inline-block mt-2 text-sm text-[#8B2E2E] hover:underline font-medium">${language === 'de' ? 'Alle Bestellungen ansehen' : 'View all orders'} &rarr;</a>
    </div>`;

    addBotMessage(html, true);
  };

  const handleTrackingSearch = async () => {
    if (!trackingInput.trim()) return;

    const trackingNum = trackingInput.trim();
    setIsTyping(true);
    addUserMessage(trackingNum);
    setTrackingInput('');
    setShowTrackingInput(false);

    const order = await fetchOrderByTracking(trackingNum);
    await delay(getTypingDelay());
    const statusTrans = STATUS_TRANSLATIONS[language] || STATUS_TRANSLATIONS.de;

    if (!order) {
      addBotMessage(language === 'de'
        ? `Leider konnte keine Bestellung mit der Tracking-Nummer "${trackingNum}" gefunden werden. Bitte uberprufen Sie die Nummer.`
        : `Unfortunately, no order with tracking number "${trackingNum}" could be found. Please check the number.`);
      return;
    }

    const statusColor = order.status === 'delivered' ? 'text-green-600 bg-green-50'
      : order.status === 'shipped' ? 'text-blue-600 bg-blue-50'
      : order.status === 'cancelled' ? 'text-red-600 bg-red-50'
      : 'text-amber-600 bg-amber-50';

    let html = `<div class="space-y-3">
      <div class="flex items-center gap-2">
        <Package class="w-5 h-5 text-[#8B2E2E]" />
        <span class="font-medium">${texts.orderStatus}</span>
      </div>
      <div class="bg-[#F2EFE9] rounded-lg p-3">
        <div class="flex justify-between items-center mb-2">
          <span class="font-mono text-sm">${order.tracking_number}</span>
          <span class="px-2 py-1 rounded-full text-xs font-medium ${statusColor}">${statusTrans[order.status] || order.status}</span>
        </div>
        <div class="text-sm text-[#5C5852]">
          <div>${texts.orderDate}: ${formatDate(order.created_at)}</div>
          <div class="font-medium text-[#8B2E2E] mt-1">${texts.total}: ${formatPrice(order.total_amount)}</div>
        </div>
        ${order.carrier_tracking_url ? `<a href="${order.carrier_tracking_url}" target="_blank" class="inline-block mt-2 text-sm text-[#8B2E2E] hover:underline">${language === 'de' ? 'Sendungsverfolgung offnen' : 'Open tracking'} &rarr;</a>` : ''}
      </div>
      <a href="/tracking" class="inline-block text-sm text-[#8B2E2E] hover:underline font-medium">${language === 'de' ? 'Zur Tracking-Seite' : 'Go to tracking page'} &rarr;</a>
    </div>`;

    addBotMessage(html, true);
  };

  const handleContactQuery = () => {
    setIsTyping(true);
    setTimeout(() => {
      const html = `<div class="space-y-3">
        <p class="font-medium">${language === 'de' ? 'So erreichen Sie uns:' : 'How to reach us:'}</p>
        <div class="space-y-2">
          <div class="bg-[#F2EFE9] rounded-lg p-3">
            <div class="flex items-start gap-3">
              <MapPin class="w-5 h-5 text-[#8B2E2E] mt-0.5" />
              <div>
                <div class="font-medium">Hermann Bohmer</div>
                <div class="text-sm text-[#5C5852]">Durnstein 244</div>
                <div class="text-sm text-[#5C5852]">3601 Durnstein, Osterreich</div>
              </div>
            </div>
          </div>
          <div class="bg-[#F2EFE9] rounded-lg p-3">
            <div class="flex items-center gap-3">
              <Phone class="w-5 h-5 text-[#8B2E2E]" />
              <div>
                <a href="tel:+436502711237" class="text-[#8B2E2E] hover:underline">+43 650 2711237</a>
              </div>
            </div>
          </div>
          <div class="bg-[#F2EFE9] rounded-lg p-3">
            <div class="flex items-center gap-3">
              <svg class="w-5 h-5 text-[#8B2E2E]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              <div>
                <a href="mailto:info@hermann-boehmer.com" class="text-[#8B2E2E] hover:underline">info@hermann-boehmer.com</a>
              </div>
            </div>
          </div>
        </div>
        <p class="text-xs text-[#969088]">${language === 'de'
          ? 'Wir antworten in der Regel innerhalb von 24 Stunden auf Ihre Anfragen.'
          : 'We usually respond to your inquiries within 24 hours.'}</p>
        <a href="/contact" class="inline-block mt-1 text-sm text-[#8B2E2E] hover:underline font-medium">${language === 'de' ? 'Zum Kontaktformular' : 'To contact form'} &rarr;</a>
      </div>`;
      addBotMessage(html, true);
    }, getTypingDelay());
  };

  const handleCouponsQuery = async () => {
    setIsTyping(true);
    setTimeout(() => {
      const html = `<div class="space-y-3">
        <p class="font-medium">${language === 'de' ? 'Aktuelle Angebote fur Sie:' : 'Current offers for you:'}</p>
        <div class="bg-gradient-to-r from-[#8B2E2E] to-[#A94442] rounded-lg p-3 text-white">
          <div class="text-xs uppercase tracking-wider opacity-80">${language === 'de' ? 'Gutscheincode' : 'Coupon Code'}</div>
          <div class="font-mono text-xl font-bold mt-1">WILLKOMMEN10</div>
          <div class="text-sm mt-1 opacity-90">${language === 'de' ? '10% Rabatt fur Neukunden' : '10% discount for new customers'}</div>
        </div>
        <div class="bg-[#F2EFE9] rounded-lg p-3">
          <div class="flex items-center gap-2">
            <Truck class="w-4 h-4 text-green-600" />
            <span class="text-sm font-medium text-green-600">${language === 'de' ? 'Gratis Versand ab 60 EUR (AT)' : 'Free shipping from 60 EUR (AT)'}</span>
          </div>
        </div>
        <p class="text-xs text-[#969088]">${language === 'de'
          ? 'Geben Sie den Code im Warenkorb ein, um den Rabatt zu erhalten.'
          : 'Enter the code in the cart to receive the discount.'}</p>
      </div>`;
      addBotMessage(html, true);
    }, getTypingDelay());
  };

  const handlePaymentQuery = () => {
    setIsTyping(true);
    setTimeout(() => {
      const html = `<div class="space-y-3">
        <p class="font-medium">${language === 'de' ? 'Wir akzeptieren folgende Zahlungsarten:' : 'We accept the following payment methods:'}</p>
        <div class="grid grid-cols-2 gap-2">
          <div class="bg-[#F2EFE9] rounded-lg p-2 text-center">
            <div class="text-2xl mb-1">💳</div>
            <div class="text-xs font-medium">Visa / Mastercard</div>
          </div>
          <div class="bg-[#F2EFE9] rounded-lg p-2 text-center">
            <div class="text-2xl mb-1">🏦</div>
            <div class="text-xs font-medium">SEPA</div>
          </div>
          <div class="bg-[#F2EFE9] rounded-lg p-2 text-center">
            <div class="text-2xl mb-1">🍎</div>
            <div class="text-xs font-medium">Apple Pay</div>
          </div>
          <div class="bg-[#F2EFE9] rounded-lg p-2 text-center">
            <div class="text-2xl mb-1">📱</div>
            <div class="text-xs font-medium">Google Pay</div>
          </div>
        </div>
        <div class="flex items-center gap-2 text-sm text-[#5C5852]">
          <svg class="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
          <span>${language === 'de' ? 'Sichere Zahlung uber Stripe' : 'Secure payment via Stripe'}</span>
        </div>
      </div>`;
      addBotMessage(html, true);
    }, getTypingDelay());
  };

  const handleQuickAction = (action) => {
    const actionLabel = quickActions.find(a => a.action === action)?.label || action;
    addUserMessage(actionLabel);

    switch(action) {
      case 'products':
        handleProductsQuery();
        break;
      case 'shipping':
        handleShippingQuery();
        break;
      case 'my_order':
        handleMyOrderQuery();
        break;
      case 'contact':
        handleContactQuery();
        break;
      case 'coupons':
        handleCouponsQuery();
        break;
      case 'payment':
        handlePaymentQuery();
        break;
      default:
        break;
    }
  };

  const processMessage = async (text) => {
    const lowerText = text.toLowerCase();

    const productKeywords = language === 'de'
      ? ['produkt', 'verkauf', 'angebot', 'sortiment', 'was gibt', 'was habt', 'was bietet', 'kaufen', 'bestellen', 'marille', 'likor', 'marmelade', 'schokolade', 'praline', 'chutney', 'brand', 'schnaps']
      : ['product', 'sell', 'offer', 'range', 'what do you', 'buy', 'order', 'apricot', 'liqueur', 'jam', 'chocolate', 'praline', 'chutney', 'brandy'];

    const shippingKeywords = language === 'de'
      ? ['versand', 'lieferung', 'liefern', 'schicken', 'kosten', 'porto', 'dauer', 'wie lange', 'deutschland', 'schweiz']
      : ['shipping', 'delivery', 'send', 'cost', 'postage', 'how long', 'germany', 'switzerland'];

    const orderKeywords = language === 'de'
      ? ['bestellung', 'bestellt', 'order', 'tracking', 'wo ist', 'meine bestellung', 'status', 'paket']
      : ['order', 'ordered', 'tracking', 'where is', 'my order', 'status', 'package'];

    const contactKeywords = language === 'de'
      ? ['kontakt', 'erreichen', 'telefon', 'email', 'adresse', 'offnungszeit', 'standort', 'durnstein', 'anrufen']
      : ['contact', 'reach', 'phone', 'email', 'address', 'hours', 'location', 'durnstein', 'call'];

    const couponKeywords = language === 'de'
      ? ['gutschein', 'rabatt', 'code', 'coupon', 'discount', 'sparen', 'aktion', 'angebot', 'prozent']
      : ['coupon', 'discount', 'code', 'promo', 'save', 'offer', 'percent'];

    const paymentKeywords = language === 'de'
      ? ['zahlung', 'bezahlen', 'kreditkarte', 'visa', 'mastercard', 'paypal', 'klarna', 'apple pay']
      : ['payment', 'pay', 'credit card', 'visa', 'mastercard', 'paypal', 'klarna', 'apple pay'];

    const greetingKeywords = language === 'de'
      ? ['hallo', 'hi', 'hey', 'servus', 'guten tag', 'guten morgen', 'guten abend', 'gruss']
      : ['hello', 'hi', 'hey', 'good morning', 'good evening', 'good day'];

    const thankKeywords = language === 'de' ? ['danke', 'vielen dank', 'thanks'] : ['thank', 'thanks'];
    const byeKeywords = language === 'de' ? ['tschuss', 'bye', 'auf wiedersehen', 'ciao'] : ['bye', 'goodbye', 'see you'];

    if (greetingKeywords.some(k => lowerText.includes(k))) {
      setIsTyping(true);
      setTimeout(() => {
        addBotMessage(language === 'de'
          ? `Guten Tag! Wie kann ich Ihnen behilflich sein? Sie konnen mich zu unseren Produkten, Versandkosten, Ihrer Bestellung oder unserem Standort befragen.`
          : `Good day! How may I assist you? You can ask me about our products, shipping costs, your order, or our location.`);
      }, getTypingDelay());
      return;
    }

    if (thankKeywords.some(k => lowerText.includes(k))) {
      setIsTyping(true);
      setTimeout(() => {
        addBotMessage(language === 'de'
          ? `Sehr gerne! Wenn Sie weitere Fragen haben, stehe ich Ihnen jederzeit zur Verfugung.`
          : `You're welcome! If you have any further questions, I'm always here to help.`);
      }, getTypingDelay());
      return;
    }

    if (byeKeywords.some(k => lowerText.includes(k))) {
      setIsTyping(true);
      setTimeout(() => {
        addBotMessage(language === 'de'
          ? `Auf Wiedersehen! Vielen Dank fur Ihr Interesse an Hermann Bohmer. Besuchen Sie uns bald wieder!`
          : `Goodbye! Thank you for your interest in Hermann Bohmer. Visit us again soon!`);
      }, getTypingDelay());
      return;
    }

    if (productKeywords.some(k => lowerText.includes(k))) {
      await handleProductsQuery();
      return;
    }

    if (shippingKeywords.some(k => lowerText.includes(k))) {
      await handleShippingQuery();
      return;
    }

    if (orderKeywords.some(k => lowerText.includes(k))) {
      await handleMyOrderQuery();
      return;
    }

    if (contactKeywords.some(k => lowerText.includes(k))) {
      handleContactQuery();
      return;
    }

    if (couponKeywords.some(k => lowerText.includes(k))) {
      await handleCouponsQuery();
      return;
    }

    if (paymentKeywords.some(k => lowerText.includes(k))) {
      handlePaymentQuery();
      return;
    }

    setIsTyping(true);
    setTimeout(() => {
      addBotMessage(language === 'de'
        ? `Entschuldigung, ich habe Ihre Anfrage nicht ganz verstanden. Ich kann Ihnen bei folgenden Themen helfen:\n\n- Unsere Produkte und das Sortiment\n- Versandkosten und Lieferzeiten\n- Ihre Bestellungen und Tracking\n- Kontaktdaten und Standort\n- Gutscheine und Rabatte\n- Zahlungsmoglichkeiten\n\nBitte formulieren Sie Ihre Frage neu oder nutzen Sie die Schnellauswahl unten.`
        : `Sorry, I didn't quite understand your request. I can help you with the following topics:\n\n- Our products and range\n- Shipping costs and delivery times\n- Your orders and tracking\n- Contact details and location\n- Coupons and discounts\n- Payment options\n\nPlease rephrase your question or use the quick selection below.`);
    }, getTypingDelay());
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const text = input.trim();
    addUserMessage(text);
    setInput('');

    await processMessage(text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-[#8B2E2E] to-[#722525] text-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all ${isOpen ? 'hidden' : ''}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
      >
        <MessageCircle size={24} />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white">
          <span className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75" />
        </span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[360px] h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#E5E0D8]"
            style={{ maxHeight: 'calc(100vh - 100px)', maxWidth: 'calc(100vw - 48px)' }}
          >
            <div className="bg-gradient-to-r from-[#8B2E2E] to-[#722525] text-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <Bot size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{texts.headerTitle}</h3>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-green-400 rounded-full" />
                      <span className="text-xs text-white/80">{texts.online}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              {isLoggedIn && user && (
                <div className="mt-2 pt-2 border-t border-white/20 text-sm">
                  <span className="text-white/70">{texts.welcomeBack}, </span>
                  <span className="font-medium">{user.first_name}</span>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAFAF8]">
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl ${
                      message.type === 'user'
                        ? 'bg-gradient-to-br from-[#8B2E2E] to-[#722525] text-white rounded-br-md'
                        : 'bg-white border border-[#E5E0D8] text-[#2D2A26] rounded-bl-md shadow-sm'
                    }`}
                  >
                    {message.isHtml ? (
                      <div
                        className="text-sm prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: message.content }}
                      />
                    ) : (
                      <p className="text-sm whitespace-pre-line">{message.content}</p>
                    )}
                    <p className={`text-[10px] mt-1.5 ${message.type === 'user' ? 'text-white/60' : 'text-[#969088]'}`}>
                      {message.time.toLocaleTimeString(language === 'en' ? 'en-US' : 'de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#E5E0D8] p-3 rounded-2xl rounded-bl-md shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-[#8B2E2E] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-[#8B2E2E] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-[#8B2E2E] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-[#969088]">{texts.typing}</span>
                    </div>
                  </div>
                </div>
              )}

              {showTrackingInput && (
                <div className="bg-white border border-[#E5E0D8] p-3 rounded-xl shadow-sm">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={trackingInput}
                      onChange={(e) => setTrackingInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleTrackingSearch()}
                      placeholder={texts.trackingPlaceholder}
                      className="flex-1 px-3 py-2 border border-[#E5E0D8] rounded-lg text-sm focus:outline-none focus:border-[#8B2E2E]"
                    />
                    <button
                      onClick={handleTrackingSearch}
                      className="px-3 py-2 bg-[#8B2E2E] text-white rounded-lg text-sm hover:bg-[#722525] transition-colors flex items-center gap-1"
                    >
                      <Search size={14} />
                    </button>
                  </div>
                  <a href="/login" className="inline-flex items-center gap-1 mt-2 text-xs text-[#8B2E2E] hover:underline">
                    <LogIn size={12} />
                    {texts.loginButton}
                  </a>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {messages.length <= 2 && !showTrackingInput && (
              <div className="px-4 py-3 bg-white border-t border-[#E5E0D8]">
                <p className="text-xs text-[#969088] mb-2 font-medium">{texts.quickQuestionsLabel}</p>
                <div className="grid grid-cols-2 gap-2">
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuickAction(action.action)}
                      className="flex items-center gap-2 text-xs px-3 py-2 bg-[#F2EFE9] text-[#5C5852] rounded-lg hover:bg-[#E5E0D8] transition-colors text-left"
                    >
                      <action.icon size={14} className="text-[#8B2E2E] flex-shrink-0" />
                      <span className="truncate">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4 bg-white border-t border-[#E5E0D8]">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={texts.placeholder}
                  className="flex-1 px-4 py-2.5 border border-[#E5E0D8] rounded-full text-sm focus:outline-none focus:border-[#8B2E2E] focus:ring-2 focus:ring-[#8B2E2E]/10 transition-all"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping}
                  className="w-10 h-10 bg-gradient-to-br from-[#8B2E2E] to-[#722525] text-white rounded-full flex items-center justify-center hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
